import { DownloadOutlined, QuestionCircleOutlined } from '../../components/icons'
import { useToast } from '../../components/ui/ToastProvider'
import type { Order } from './domain'
import { isSafeDocumentReference } from './domain'

export interface ActionBarProps {
  readonly order: Order
  /** Reorder this order into the cart (UID-94). When provided, the Reorder button
   * is live; when omitted it stays disabled like the other not-yet-backed actions. */
  readonly onReorder?: (() => void) | undefined
  /** True while a reorder is in flight — disables the button and shows a busy label. */
  readonly reordering?: boolean | undefined
}

/**
 * Order-detail action bar (UX spec §2.2 item 1 / §2.3), reconciled to the
 * prototype's own button treatment (`Fictiv Platform.dc.html`, "ORDER
 * DETAIL" section, ~L3261-3267). S6/UID-100 D4: every button is now a real,
 * enabled, working action (this file is "fully yours" per the ticket — no
 * more disabled/placeholder buttons):
 *
 * - **Support**: DECISION (no backing support-ticketing endpoint exists in
 *   this contract slice) — opens the user's mail client via a `mailto:` link
 *   pre-filled with the order id, a real, low-risk action needing no
 *   backend. Recorded here rather than silently guessed. UID-317: icon is
 *   `QuestionCircleOutlined` (help/question-circle — the design's icon,
 *   `Fictiv Platform.dc.html:3867`), not a headset glyph.
 * - **Share**: the Web Share API (`navigator.share`) with a clipboard-copy +
 *   toast fallback — real, working, no backend required. Plain text, no
 *   icon (UID-123 item 3; `Fictiv Platform.dc.html`'s Share button has no
 *   icon, unlike Support/Parts and drawings/Order confirmation).
 * - **Parts and drawings**: opens the first `annotated_pdf` document (safe-
 *   reference-checked, same {@link isSafeDocumentReference} ShipmentsPanel
 *   uses — one shared home, no duplicated validation logic); degrades to a
 *   toast when `order.documents` has none (Gate B: always `[]` today) rather
 *   than staying disabled, per the ticket's explicit instruction. UID-317:
 *   icon is `DownloadOutlined` (`.dc.html:3870`), not a generic file icon.
 * - **Order confirmation**: same pattern, filtered to `kind ===
 *   'order_confirmation'`. UID-317: icon is also `DownloadOutlined`
 *   (`.dc.html:3871`).
 * - **Reorder**: unchanged (UID-94) — re-adds the order's lines to the cart.
 *
 * Accessibility: every action is a native `<button>` (keyboard-operable,
 * existing focus/contrast styling untouched); toast-driven feedback reuses
 * `useToast`'s existing `aria-live="polite"` region rather than inventing a
 * new announcement pattern.
 */
export function ActionBar({ order, onReorder, reordering = false }: ActionBarProps) {
  const { showToast } = useToast()

  const handleSupport = () => {
    // DECISION: no support-ticketing endpoint exists yet — a real, working,
    // low-risk action (mailto:) rather than a fake no-op.
    window.location.href = `mailto:support@fictiv.com?subject=${encodeURIComponent(
      `Order ${order.id}`,
    )}`
  }

  const handleShare = async () => {
    const url = `${window.location.origin}/orders/${order.id}`
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ url })
      } catch {
        // The native share sheet rejects both on user-cancel (an AbortError,
        // by far the common case) AND on a genuine failure. We DELIBERATELY
        // treat every rejection as a cancellation — silent, no toast: a
        // failed share is low-stakes (the URL is still on screen) and a
        // scary error toast on a cancel would be worse than staying quiet.
      }
      return
    }
    try {
      await navigator.clipboard.writeText(url)
      showToast('Link copied to clipboard', 'success')
    } catch {
      showToast("Couldn't copy the link. Try again.", 'error')
    }
  }

  const handlePartsAndDrawings = () => {
    const documents = order.documents ?? []
    const annotatedPdf = documents.find((doc) => doc.kind === 'annotated_pdf')
    if (annotatedPdf && isSafeDocumentReference(annotatedPdf.reference)) {
      window.open(annotatedPdf.reference, '_blank', 'noopener,noreferrer')
      return
    }
    showToast('No parts and drawings available for this order yet', 'info')
  }

  const handleOrderConfirmation = () => {
    const documents = order.documents ?? []
    const confirmation = documents.find((doc) => doc.kind === 'order_confirmation')
    if (confirmation && isSafeDocumentReference(confirmation.reference)) {
      window.open(confirmation.reference, '_blank', 'noopener,noreferrer')
      return
    }
    showToast("Order confirmation document isn't available yet", 'info')
  }

  return (
    <fieldset className="action-bar">
      <legend className="sr-only">Order actions</legend>
      <button type="button" className="btn btn-outline btn-md" onClick={handleSupport}>
        <QuestionCircleOutlined aria-hidden="true" /> Support
      </button>
      <button type="button" className="btn btn-outline btn-md" onClick={() => void handleShare()}>
        Share
      </button>
      <button
        type="button"
        className="btn btn-outline btn-md"
        disabled={onReorder === undefined || reordering}
        {...(onReorder === undefined ? { title: 'Not available in this release' } : {})}
        onClick={onReorder}
      >
        {reordering ? 'Adding to cart…' : 'Reorder'}
      </button>
      <button type="button" className="btn btn-outline btn-md" onClick={handlePartsAndDrawings}>
        <DownloadOutlined aria-hidden="true" /> Parts and drawings
      </button>
      <button type="button" className="btn btn-primary btn-md" onClick={handleOrderConfirmation}>
        <DownloadOutlined aria-hidden="true" /> Order confirmation
      </button>
    </fieldset>
  )
}
